"use client";

import { useEffect, useState } from "react";
import { ArrowLeft, Menu, Search, User, X } from "lucide-react";
import { ROUTES } from "@/lib/routes";
import { Link } from "@/i18n/routing";
import { LJ_CATEGORIES, LJ_NAV_SIDE_LINKS } from "@/fixtures/lj/fixtures";
import { LJ_COLORS, LJ_FONT, LJ_TYPE } from "@/lib/theme/lj";

/**
 * Vogue-style header.
 *   Row 1 — "← Back to HKEJ" left, wordmark center, search + hamburger right
 *   Row 2 — single horizontal strip of category links (no section links)
 *
 * BLOG / VIDEO / FEATURES / CURRENT ISSUE live exclusively inside the
 * right-side slide-in drawer, opened by the hamburger. The drawer is the
 * same on every breakpoint.
 */
export function Nav() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  return (
    <header
      className="sticky top-0 z-50 w-full border-b xl:border-b-0"
      style={{
        backgroundColor: LJ_COLORS.bgPage,
        borderBottomColor: LJ_COLORS.dividerHair,
        boxShadow: scrolled ? `0 1px 0 ${LJ_COLORS.dividerHair}` : "none",
        transition: "box-shadow .2s ease-in",
        fontFamily: LJ_FONT.sans,
        color: LJ_COLORS.textPrimary,
      }}
    >
      {/* Row 1 — masthead (always visible, the sticky surface) */}
      <div className="mx-auto flex w-full max-w-[1440px] items-center justify-between px-4 py-2 sm:grid sm:grid-cols-[1fr_auto_1fr] lg:px-[120px] lg:py-3">
        {/* Left — Back to HKEJ (solid dark button) — hidden at <sm; lives in the drawer there */}
        <div className="hidden sm:flex items-center">
          <Link
            href="/"
            aria-label="Back to HKEJ"
            className="inline-flex items-center gap-2 px-3 py-2 transition-opacity hover:opacity-80"
            style={{
              ...LJ_TYPE.navLink,
              fontSize: 11,
              color: LJ_COLORS.textOnDark,
              backgroundColor: LJ_COLORS.textPrimary,
            }}
          >
            <span>Back to HKEJ</span>
          </Link>
        </div>

        {/* Wordmark — flush-left on mobile, centered at sm+ */}
        <Link
          href="/lj"
          className="block text-left sm:text-center"
          aria-label="Lifestyle Journal home"
        >
          <span
            style={{
              ...LJ_TYPE.wordmark,
              fontSize: "clamp(22px, 3.2vw, 40px)",
              lineHeight: 1,
              color: LJ_COLORS.textPrimary,
            }}
          >
            LIFESTYLE&nbsp;JOURNAL
          </span>
        </Link>

        {/* Right — search · account · MENU + hamburger */}
        <div className="flex items-center justify-end gap-1 sm:gap-2">
          {/* Was a bare <button> with no handler and no panel, so the icon
              looked live but nothing happened. QA: "沒有搜尋框". Points at the
              site search route, matching what the main HKEJ nav does
              (components/shared/nav/nav-actions.tsx). */}
          <Link
            // The vertical's OWN search — this pointed at the MAIN site's
            // `/search`, so a reader looking for an LJ story left the magazine.
            href="/lj/search"
            aria-label="Search"
            className="inline-flex h-9 w-9 items-center justify-center hover:opacity-60 transition-opacity"
          >
            <Search className="h-[18px] w-[18px]" strokeWidth={1.5} />
          </Link>
          {/* Was a bare <button> with no handler: the icon looked live and did
              nothing. Points at the member area, matching what the search icon
              beside it already does and what the main HKEJ nav uses. */}
          <Link
            href={ROUTES.profile}
            aria-label="Account"
            className="hidden sm:inline-flex h-9 w-9 items-center justify-center hover:opacity-60 transition-opacity"
          >
            <User className="h-[18px] w-[18px]" strokeWidth={1.5} />
          </Link>
          <button
            type="button"
            aria-label="Open menu"
            onClick={() => setDrawerOpen(true)}
            className="inline-flex items-center gap-2 px-2 py-2 hover:opacity-60 transition-opacity"
          >
            <span
              className="hidden sm:inline"
              style={{
                ...LJ_TYPE.navLink,
                fontSize: 11,
                color: LJ_COLORS.textPrimary,
              }}
            >
              MENU
            </span>
            <Menu className="h-5 w-5" strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {/* Row 2 — categories (collapses when scrolled so only masthead stays visible) */}
      <nav
        aria-hidden={scrolled}
        className={`w-full overflow-hidden transition-[max-height,opacity,border-color] duration-200 ease-in-out ${
          scrolled
            ? "max-h-0 opacity-0"
            : "hidden xl:block max-h-[80px] opacity-100"
        }`}
        style={{
          borderTop: scrolled
            ? "1px solid transparent"
            : `1px solid ${LJ_COLORS.dividerHair}`,
          borderBottom: scrolled
            ? "1px solid transparent"
            : `1px solid ${LJ_COLORS.dividerHair}`,
        }}
      >
        <div className="mx-auto flex w-full max-w-[1440px] items-center justify-center gap-x-8 px-4 lg:px-[120px]">
          <div className="flex items-center gap-x-8 overflow-x-auto py-3">
            {LJ_CATEGORIES.map((cat) => (
              <Link
                key={cat.label}
                href={cat.href}
                style={{
                  ...LJ_TYPE.navLink,
                  fontWeight: 400,
                  color: LJ_COLORS.textPrimary,
                }}
                className="whitespace-nowrap hover:opacity-60 transition-opacity"
              >
                {cat.label}
              </Link>
            ))}
          </div>
        </div>
      </nav>

      {/* Right-side drawer (always mounted; slides via transform) */}
      <>
        <button
          type="button"
          aria-label="Close menu overlay"
          tabIndex={drawerOpen ? 0 : -1}
          onClick={() => setDrawerOpen(false)}
          className={`fixed inset-0 z-[55] bg-black/30 transition-opacity duration-300 ease-out ${
            drawerOpen ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
        />
        {/*
          The closed drawer is INVISIBLE, and only the open one casts a shadow.

          `position: fixed; right: 0` resolves against the initial containing
          block, which excludes the scrollbar gutter `scrollbar-gutter: stable`
          reserves — so `translate-x-full` left the panel's edge 15px short of
          the window and it sat in the gutter as a white strip with `shadow-xl`
          bleeding out of it, down the whole right-hand side of every page. QA:
          "remove the box shadow on the right scrollbar white rectangle".

          `invisible` (not `hidden`) so the transform still animates on open;
          `visibility` is transitioned with a delay on the way out so the panel
          stays visible for the duration of the slide.
        */}
        <aside
          className={`fixed inset-y-0 right-0 z-[60] flex w-full max-w-[420px] flex-col bg-white transition-[transform,visibility] duration-300 ease-out will-change-transform ${
            drawerOpen
              ? "visible translate-x-0 shadow-xl"
              : "invisible translate-x-full pointer-events-none"
          }`}
          role="dialog"
          aria-modal="true"
          aria-hidden={!drawerOpen}
        >
          <div
            className="flex items-center justify-between px-6"
            style={{
              borderBottom: `1px solid ${LJ_COLORS.dividerHair}`,
              paddingTop: 15,
              paddingBottom: 15,
            }}
          >
            <span
              style={{
                ...LJ_TYPE.wordmark,
                fontSize: 20,
                color: LJ_COLORS.textPrimary,
              }}
            >
              LIFESTYLE JOURNAL
            </span>
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              aria-label="Close menu"
              className="inline-flex items-center gap-2 px-1 py-2 hover:opacity-60 transition-opacity"
            >
              <span
                style={{
                  ...LJ_TYPE.navLink,
                  fontSize: 11,
                  fontWeight: 400,
                  color: LJ_COLORS.textPrimary,
                }}
              >
                CLOSE
              </span>
              <X className="h-5 w-5" strokeWidth={1.5} />
            </button>
          </div>
          <div className="flex flex-1 flex-col overflow-y-auto px-6 py-6">
            {/* Regular items — categories, dark text */}
            {LJ_CATEGORIES.map((cat) => (
              <Link
                key={cat.label}
                href={cat.href}
                onClick={() => setDrawerOpen(false)}
                style={{
                  ...LJ_TYPE.navLink,
                  fontSize: 13,
                  fontWeight: 400,
                  color: LJ_COLORS.textPrimary,
                }}
                className="block py-3"
              >
                {cat.label}
              </Link>
            ))}

            {/* MORE section — BLOG / VIDEO / FEATURES / CURRENT ISSUE */}
            <div
              className="mt-6 flex flex-col gap-1 pt-6"
              style={{ borderTop: `1px solid ${LJ_COLORS.dividerHair}` }}
            >
              <h4
                className="pb-2"
                style={{
                  ...LJ_TYPE.micro,
                  fontWeight: 400,
                  color: LJ_COLORS.textSecondary,
                }}
              >
                More
              </h4>
              {LJ_NAV_SIDE_LINKS.map((item) => (
                <Link
                  key={item.label}
                  href={item.href}
                  onClick={() => setDrawerOpen(false)}
                  style={{
                    ...LJ_TYPE.navLink,
                    fontSize: 13,
                    color: LJ_COLORS.textPrimary,
                  }}
                  className="block py-3"
                >
                  {item.label}
                </Link>
              ))}
            </div>

            {/* Search row */}
            <div
              className="mt-6 pt-6"
              style={{ borderTop: `1px solid ${LJ_COLORS.dividerHair}` }}
            >
              {/* The masthead search was pointed at /search in an earlier pass;
                  this drawer copy was missed and stayed a handler-less button. */}
              <Link
                href="/lj/search"
                aria-label="Search"
                onClick={() => setDrawerOpen(false)}
                className="flex w-full items-center gap-3 py-3 hover:opacity-60 transition-opacity"
              >
                <Search
                  className="h-[18px] w-[18px]"
                  style={{ color: LJ_COLORS.textPrimary }}
                  strokeWidth={1.5}
                />
                <span
                  style={{
                    ...LJ_TYPE.navLink,
                    fontSize: 13,
                    fontWeight: 400,
                    color: LJ_COLORS.textPrimary,
                  }}
                >
                  Search
                </span>
              </Link>
              {/* Account + Back to HKEJ — masthead-only items relocated for <sm */}
              <Link
                href={ROUTES.profile}
                aria-label="Account"
                onClick={() => setDrawerOpen(false)}
                className="flex w-full items-center gap-3 py-3 sm:hidden hover:opacity-60 transition-opacity"
              >
                <User
                  className="h-[18px] w-[18px]"
                  style={{ color: LJ_COLORS.textPrimary }}
                  strokeWidth={1.5}
                />
                <span
                  style={{
                    ...LJ_TYPE.navLink,
                    fontSize: 13,
                    fontWeight: 400,
                    color: LJ_COLORS.textPrimary,
                  }}
                >
                  Account
                </span>
              </Link>
              <Link
                href="/"
                onClick={() => setDrawerOpen(false)}
                aria-label="Back to HKEJ"
                className="flex w-full items-center gap-3 py-3 sm:hidden hover:opacity-60 transition-opacity"
              >
                <ArrowLeft
                  className="h-[18px] w-[18px]"
                  style={{ color: LJ_COLORS.textPrimary }}
                  strokeWidth={1.5}
                />
                <span
                  style={{
                    ...LJ_TYPE.navLink,
                    fontSize: 13,
                    fontWeight: 400,
                    color: LJ_COLORS.textPrimary,
                  }}
                >
                  Back to HKEJ
                </span>
              </Link>
            </div>
          </div>
        </aside>
      </>
    </header>
  );
}
